import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * PlatformOverview
 *
 * Hosts the standalone Vite + React 19 presentation
 * (`Frontend/kinesight_client_materials`) inside a full-viewport iframe so
 * the deck renders byte-for-byte as it was authored — no changes to its
 * layout or contents. The static build is dropped into
 * `Frontend/public/platform-deck/` (Vite `base: '/platform-deck/'`) and is
 * served by CRA / Nginx as a sub-app at `/platform-deck/index.html`.
 *
 * The overlay is `position: fixed` with a z-index above the global header
 * and 3D sphere background, so the deck experience occupies the screen
 * exactly as it does standalone. A discreet "Home" pill in the top-left
 * (clear of the deck's own bottom-edge chrome) lets visitors return to
 * the marketing site.
 */
const PlatformOverview = () => {
  const frameRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const focusFrame = () => {
      try {
        node.focus({ preventScroll: true });
        node.contentWindow?.focus();
      } catch (_) {
        // Cross-origin or detached frame — keyboard nav still works once the
        // user clicks inside the deck.
      }
    };
    if (node.contentDocument?.readyState === 'complete') {
      focusFrame();
    } else {
      node.addEventListener('load', focusFrame, { once: true });
    }
    return () => node.removeEventListener('load', focusFrame);
  }, []);

  return (
    <div
      className="fixed inset-0 bg-[#0B1120]"
      style={{ zIndex: 100 }}
      role="region"
      aria-label="Kinesight platform overview"
    >
      <iframe
        ref={frameRef}
        src="/platform-deck/index.html"
        title="Kinesight — Platform Overview"
        className="block w-full h-full border-0"
        allow="fullscreen; clipboard-read; clipboard-write"
      />

      <Link
        to="/"
        className="fixed top-5 left-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/45 px-3.5 py-2 text-[0.65rem] font-mono uppercase tracking-[0.3em] text-white/75 backdrop-blur-md transition-colors duration-200 hover:border-teal/40 hover:text-teal"
        style={{ zIndex: 101 }}
        aria-label="Back to Kinesight home"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Home</span>
      </Link>
    </div>
  );
};

export default PlatformOverview;
